import { useMutation } from '@tanstack/react-query';

import {
  deleteV1IdentityProviderById,
  postV1IdentityProvider,
  putV1IdentityProviderById,
  type IdentityProviderPatchBody,
  type IdentityProviderPostBody,
} from '../../../api/generated/pap';
import { getV1IdentityProvidersQueryKey } from '../../../api/generated/pap/@tanstack/react-query.gen';
import { queryClient } from '../../../api/queryClient';

/**
 * Mutation hooks for the Identity Providers feature.
 *
 * Each hook wraps the generated SDK call (with `throwOnError` so failures
 * reject) and invalidates the list query on success using the generated query
 * key. The `If-Match` header for PUT requests is attached automatically by the
 * shared request interceptor (`src/api/clients/interceptors.ts`).
 */

const invalidateIdentityProviders = () =>
  queryClient.invalidateQueries({ queryKey: getV1IdentityProvidersQueryKey() });

export const useAddIdentityProvider = () =>
  useMutation({
    mutationFn: async (payload: IdentityProviderPostBody) => {
      const { data } = await postV1IdentityProvider({ body: payload, throwOnError: true });
      return data;
    },
    onSuccess: invalidateIdentityProviders,
  });

export const useUpdateIdentityProvider = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IdentityProviderPatchBody }) => {
      const { data } = await putV1IdentityProviderById({
        path: { id },
        body: payload,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateIdentityProviders,
  });

export const useDeleteIdentityProvider = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteV1IdentityProviderById({ path: { id }, throwOnError: true });
      return data;
    },
    onSuccess: invalidateIdentityProviders,
  });
